package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.Customer
import com.solevo.dist.application.models.User
import com.solevo.dist.network.api.user.UserRemoteSource
import com.solevo.dist.repository.ApplicationCustomerRepository
import com.solevo.dist.util.AmountFormatter
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.launch
import org.koin.mp.KoinPlatform.getKoin
import kotlin.math.absoluteValue

data class RebateRowUi(
    val label: String,
    val valueText: String
)

data class RewardsUiState(
    val loading: Boolean = true,
    val error: String? = null,
    val accruedRebateText: String = "—",
    val rebateRows: List<RebateRowUi> = emptyList(),
    val creditScore: Int = 0,
    val maxCreditScore: Int = 1000,
    val paymentTermsText: String = "—",
    val creditLineText: String = "—"
)

class RewardsViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val customerRepository: ApplicationCustomerRepository = getKoin().get()
    private val userRemoteSource: UserRemoteSource = getKoin().get()

    private val _state = MutableStateFlow(RewardsUiState())
    val state: StateFlow<RewardsUiState> = _state.asStateFlow()

    @OptIn(ExperimentalCoroutinesApi::class)
    fun start() {
        _state.value = _state.value.copy(loading = true, error = null)
        scope.launch {
            userRemoteSource.currentUser()
                .filterNotNull()
                .flatMapLatest { user ->
                    if (user.customerId.isNotEmpty()) {
                        collectCustomer()
                    } else {
                        kotlinx.coroutines.flow.flow { emit(null) }
                    }
                }
                .collectLatest { customer ->
                    emitFromCustomer(customer)
                }
        }
    }

    private suspend fun collectCustomer(): kotlinx.coroutines.flow.Flow<Customer?> {
        return customerRepository.getCurrentCustomer()
    }

    private fun emitFromCustomer(customer: Customer?) {
        if (customer == null) {
            _state.value = RewardsUiState(loading = false, error = "Customer not found")
            return
        }

        val accruedText = AmountFormatter.formatAmountNoSign(customer.rebate?.accruedRebate ?: 0.0)
        val rebateRows = customer.rebate?.rebates?.map { r ->
            RebateRowUi(label = r.name.ifBlank { r.code }, valueText = AmountFormatter.formatAmountNoSign(r.value ?: 0.0))
        } ?: emptyList()

        val paymentTermsText = if ((customer.rewards?.paymentTerms ?: 0) > 0)
            "${customer.rewards?.paymentTerms} days" else "—"

        val creditLineText = customer.creditInfo?.totalCreditLine?.let { AmountFormatter.formatAmountNoSign(it) } ?: "—"

        _state.value = RewardsUiState(
            loading = false,
            error = null,
            accruedRebateText = accruedText,
            rebateRows = rebateRows,
            creditScore = customer.rewards?.creditScore ?: 0,
            maxCreditScore = 1000,
            paymentTermsText = paymentTermsText,
            creditLineText = creditLineText
        )
    }

    fun stop() = scope.cancel()
}


